import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react'
import { cn } from '../lib/cn'
import { FieldContext } from '../lib/field-context'

export function Field({
  children,
  className,
  controlId,
  description,
  error,
  focusControlOnLabelClick = true,
  label,
  ...props
}: ComponentProps<'div'> & {
  controlId?: string
  description?: ReactNode
  error?: string
  focusControlOnLabelClick?: boolean
  label?: ReactNode
}) {
  const generatedId = useId()
  const id = controlId ?? generatedId
  const [labelSlot, setLabelSlot] = useState<string>()
  const [descriptionSlot, setDescriptionSlot] = useState<string>()
  const [errorSlot, setErrorSlot] = useState<string>()
  const registerLabel = useCallback((value: string | undefined) => setLabelSlot(value), [])
  const registerDescription = useCallback(
    (value: string | undefined) => setDescriptionSlot(value),
    [],
  )
  const registerError = useCallback((value: string | undefined) => setErrorSlot(value), [])
  const contextValue = useMemo(
    () => ({
      controlId: id,
      focusControlOnLabelClick,
      labelId: label ? `${id}-label` : labelSlot,
      descriptionId: description ? `${id}-description` : descriptionSlot,
      errorId: error ? `${id}-error` : errorSlot,
      registerDescription,
      registerLabel,
      registerError,
    }),
    [
      description,
      descriptionSlot,
      error,
      errorSlot,
      focusControlOnLabelClick,
      id,
      label,
      labelSlot,
      registerDescription,
      registerError,
      registerLabel,
    ],
  )
  return (
    <FieldContext.Provider value={contextValue}>
      <div data-slot="field" className={cn('grid gap-2', className)} {...props}>
        {label ? <FieldLabel>{label}</FieldLabel> : null}
        <FieldControl>{children}</FieldControl>
        {description ? <FieldDescription>{description}</FieldDescription> : null}
        <FieldError>{error}</FieldError>
      </div>
    </FieldContext.Provider>
  )
}
export function FieldLabel({ className, children, ...props }: ComponentProps<'label'>) {
  const field = useContext(FieldContext)
  const id = props.id ?? (field ? `${field.controlId}-label` : undefined)
  const register = field?.registerLabel
  useEffect(() => {
    if (id) register?.(id)
    return () => {
      if (id) register?.(undefined)
    }
  }, [id, register])
  return (
    <label
      {...props}
      id={id}
      htmlFor={field?.focusControlOnLabelClick ? field.controlId : props.htmlFor}
      data-slot="field-label"
      className={cn('block text-xs font-semibold text-muted-foreground', className)}
    >
      {children}
    </label>
  )
}
export function FieldControl({ className, ...props }: ComponentProps<'div'>) {
  return <div {...props} data-slot="field-control" className={cn('min-w-0', className)} />
}
export function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
  const field = useContext(FieldContext)
  const id = props.id ?? (field ? `${field.controlId}-description` : undefined)
  const register = field?.registerDescription
  useEffect(() => {
    if (id) register?.(id)
    return () => {
      if (id) register?.(undefined)
    }
  }, [id, register])
  return (
    <p
      {...props}
      id={id}
      data-slot="field-description"
      className={cn('text-xs leading-5 text-muted-foreground', className)}
    />
  )
}
export function FieldError({ className, children, ...props }: ComponentProps<'div'>) {
  const field = useContext(FieldContext)
  const id = props.id ?? (field ? `${field.controlId}-error` : undefined)
  const register = field?.registerError
  const present = Boolean(children)
  useEffect(() => {
    if (present && id) register?.(id)
    return () => {
      if (present && id) register?.(undefined)
    }
  }, [id, present, register])
  return (
    <div
      {...props}
      id={undefined}
      hidden={!present}
      aria-live="polite"
      aria-atomic="true"
      data-slot="field-error"
    >
      {children ? (
        <p id={id} className={cn('text-xs leading-5 text-danger-foreground', className)}>
          {children}
        </p>
      ) : null}
    </div>
  )
}
